import { useCallback, useEffect, useState } from "react";
import { listChatThreads } from "./chatApi.js";

// ChatPolicy.MaxPageSize on the backend is 200 and DefaultPageSize is 50.
// The inbox starts at 50 and can be raised once, by the operator, to the real
// cap - there is no page/offset parameter on this endpoint, so this is the
// whole of what "more" can honestly mean here. This hook never loops to
// prefetch several pages.
export const CHAT_INBOX_DEFAULT_PAGE_SIZE = 50;
export const CHAT_INBOX_MAX_PAGE_SIZE = 200;

/**
 * Fetches GET /api/backoffice/chat/threads - mirrors ../sos/useSosCasesList.js.
 * Refetches every time `filters` changes (search, pageSize).
 *
 * `filters` must be a stable object reference (build it with useMemo) - the
 * effect depends on the object directly so it only re-runs when a real filter
 * value changes, not on every render.
 *
 * Never fetches when `filters` is falsy, so a caller still resolving its own
 * access guard gets an inert "idle" state instead of an unconditional fetch.
 */
export function useChatInbox(filters) {
  const [state, setState] = useState({ status: "loading", data: null, error: null });
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!filters) {
      setState({ status: "idle", data: null, error: null });
      return undefined;
    }

    let cancelled = false;
    // Previous data stays visible while a refetch is in flight. This matters
    // more here than on a table screen: the inbox refetches on a timer, and
    // replacing the conversation list with a spinner every few seconds while
    // someone is reading it would be unusable.
    setState((previous) => ({ status: "loading", data: previous.data, error: null }));

    listChatThreads(filters)
      .then((data) => {
        if (!cancelled) {
          setState({ status: "ready", data, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState((previous) => ({ status: "error", data: previous.data, error }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [filters, reloadToken]);

  const refetch = useCallback(() => setReloadToken((token) => token + 1), []);

  return { ...state, refetch };
}
